// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\components\layout\Sidenav.js

import React from "react";
import { Menu } from "antd";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux"; 
import { useHasPermission } from "../../hooks/useHasPermission";
import logoLG from "../../assets/images/logoLG.png";
import { FaHospitalUser, FaBaby } from "react-icons/fa6";

import { 
  FaUserDoctor, FaHouseMedicalFlag, FaFileMedical, 
  FaMapLocationDot, FaBoxArchive, FaListUl, FaChartPie, FaChartLine 
} from "react-icons/fa6";

import { GiMedicines } from "react-icons/gi"; 
import { IoMedkit } from "react-icons/io5";
import { ImSpellCheck } from "react-icons/im";
import { TbTestPipe } from "react-icons/tb";
import {
  CalendarOutlined, ScheduleOutlined, SolutionOutlined,
  WarningOutlined, SnippetsOutlined, BarChartOutlined,
  UserOutlined, SettingOutlined, HistoryOutlined,
  ReconciliationOutlined, AppstoreOutlined, TeamOutlined,     
  ShopOutlined, CopyOutlined, CheckSquareOutlined,
  DatabaseOutlined, AimOutlined, RocketOutlined, ShoppingOutlined,
  DashboardOutlined, SafetyCertificateOutlined, CrownOutlined,
  AlertOutlined // ⚡ Import de l'icône pour les visites particulières
} from "@ant-design/icons";

function Sidenav({ color }) {
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/$/, "") || "/";
  const user = useSelector((state) => state.signin.user);

  // --- 1. HOOKS ---
  const canManageDataCorrections = useHasPermission('data:manage');
  const canViewUsers = useHasPermission('users:view');
  const canManageRoles = useHasPermission('roles:manage');
  const hasSettingsPermission = useHasPermission('settings:manage');
  const canViewDelegates = useHasPermission('delegates:view');
  const canViewReports = useHasPermission('reports:view');
  const canDeleteReports = useHasPermission('reports:delete');
  const canViewVisits = useHasPermission('visits:view');
  const canViewAnalytics = useHasPermission('analytics:view');
  const canViewDoctorsPage = useHasPermission('pages:doctors:view');
  const canViewPharmaciesPage = useHasPermission('pages:pharmacies:view');
  const canViewWholesalersPage = useHasPermission('pages:wholesalers:view');
  const canViewRegionsPage = useHasPermission('pages:regions:view');
  const canViewSpecialtiesPage = useHasPermission('pages:specialties:view');
  const canViewProductsPage = useHasPermission('pages:products:view');
  const canViewSamplesPage = useHasPermission('pages:samples:view');
  const canViewCyclesPage = useHasPermission('planning:cycles:view');
  const canViewHolidaysPage = useHasPermission('planning:holidays:view');
  const canViewLeavesPage = useHasPermission('planning:leaves:view');
  const canViewAudit = useHasPermission('audit:view');
  const canViewSupervisors = useHasPermission('supervisors:view');

  // --- 2. LOGIQUE ---
  const userRole = user?.role?.name?.toLowerCase();
  const isClinicOnly = userRole === "delegue clinique" || user?.email === "maternite@babyworld.tn";
  const isSupervisor = userRole === "superviseur";
  const isCommercial = userRole === "comptable";
  
  const isMarketingComm = userRole === "marketingcommercial" || userRole === "marketingcommerciale";
  const isSyrine = 
    user?.email?.toLowerCase().includes("syrine") || 
    user?.firstName?.toLowerCase().includes("syrine");
  
  const canManageSettings = hasSettingsPermission || canManageRoles;

  if (isClinicOnly) return null;

  const activeLinkColor = "#0e519b";
  const inactiveColor = "#67748e"; 

  const menuItems = [
    {
      key: 'section-partenaires',
      label: 'LISTE & PARTENAIRE', 
      icon: AppstoreOutlined,
      children: [
        canViewDoctorsPage && { key: "/médecins", path: "/médecins", label: "Médecin", icon: FaUserDoctor },
        canViewDoctorsPage && { key: "/médecins/archives", path: "/médecins/archives", label: "Archive", icon: FaBoxArchive },
        canManageDataCorrections && { key: "/admin/data-corrections/persons", path: "/admin/data-corrections/persons", label: "Médecins (Suggestions)", icon: CheckSquareOutlined },
        canViewPharmaciesPage && { key: "/pharmacies", path: "/pharmacies", label: "Pharmacie", icon: FaHouseMedicalFlag },
        canViewWholesalersPage && { key: "/grossistes", path: "/grossistes", label: "Grossiste", icon: GiMedicines },
        canViewSpecialtiesPage && { key: "/spécialités", path: "/spécialités", label: "Spécialité", icon: ImSpellCheck },
        canViewRegionsPage && { key: "/régions", path: "/régions", label: "Région", icon: FaMapLocationDot },
      ].filter(Boolean)
    },
    {
      key: 'section-catalogue',
      label: 'PRODUITS & ÉCHANTILLONS',
      icon: ShopOutlined,
      children: [
        canViewProductsPage && { key: "/produit", path: "/produit", label: "Produits", icon: IoMedkit },
        canViewSamplesPage && { key: "/samples", path: "/samples", label: "Échantillons", icon: TbTestPipe },
        canViewSamplesPage && { key: "/marketing-actions", path: "/marketing-actions", label: "Actions Marketing", icon: RocketOutlined },
        canViewSamplesPage && { key: "/ciblage-echantillons", path: "/ciblage-echantillons", label: "Ciblage Spécialités", icon: AimOutlined },
        canViewDelegates && { key: "/gestion-stocks", path: "/gestion-stocks", label: "Gestion des Stocks", icon: DatabaseOutlined },
        canViewSamplesPage && { key: "/summary/given-samples", path: "/summary/given-samples", label: "Résumé Échantillons", icon: SnippetsOutlined },
        canViewProductsPage && { key: "/produit/resume", path: "/produit/resume", label: "Résumé Produit", icon: ShoppingOutlined },
      ].filter(Boolean)
    },
    {
      key: 'section-rapports',
      label: 'RAPPORTS & VISITES',
      icon: FaFileMedical,
      children: [
        isSupervisor && { 
            key: "/superviseur/rapport-controle", 
            path: "/superviseur/rapport-controle", 
            label: "Visite de Contrôle", 
            icon: SafetyCertificateOutlined 
        },
        canViewReports && { key: '/rapports', path: '/rapports', label: 'Liste des Rapports', icon: FaListUl },
        
        // ⚡ NOUVEAU LIEN : Visites Particulières (Non abouties & Spécifiques) 👇
        canViewReports && { 
            key: '/visites-particulieres', 
            path: '/visites-particulieres', 
            label: 'Visites Particulières', 
            icon: AlertOutlined 
        },

        (canViewReports || isSyrine) && { 
            key: '/rapports-superviseurs', 
            path: '/rapports-superviseurs', 
            label: 'Rapports Superviseurs', 
            icon: CrownOutlined 
        },

        canManageDataCorrections && { key: "/admin/data-corrections/reports", path: "/admin/data-corrections/reports", label: "Correction Rapports", icon: CheckSquareOutlined },
        canDeleteReports && { key: '/rapports/doublons', path: '/rapports/doublons', label: 'Doublons', icon: CopyOutlined },
        canViewVisits && { key: "/visites", path: "/visites", label: "Statistiques Visites", icon: ScheduleOutlined },
        canViewVisits && { key: "/visites-manquees", path: "/visites-manquees", label: "Visites Manquées", icon: WarningOutlined },
      ].filter(Boolean)
    },
    {
      key: 'section-planning',
      label: 'PLANIFICATION',
      icon: CalendarOutlined,
      children: [
        canViewCyclesPage && { key: '/planning/cycles', path: '/planning/cycles', label: 'Cycles', icon: FaBoxArchive },
        canViewHolidaysPage && { key: '/planning/holidays', path: '/planning/holidays', label: 'Jours Fériés', icon: UserOutlined },
        canViewLeavesPage && { key: '/planning/leaves', path: '/planning/leaves', label: 'Congés', icon: ImSpellCheck },
        canViewLeavesPage && { key: '/planning/evenements', path: '/planning/evenements', label: 'Événements', icon: RocketOutlined },
      ].filter(Boolean)
    },
    {
      key: 'section-analytics',
      label: 'ANALYSES & INDICATEURS',
      icon: BarChartOutlined,
      children: [
        { key: '/analytics/return-index', path: '/analytics/return-index', label: 'Indice de Retour', icon: FaChartPie },
        { key: '/analytics/summary', path: '/analytics/summary', label: 'Résumé Délégués', icon: BarChartOutlined },
        { key: '/analytics/weekly-missed-visits', path: '/analytics/weekly-missed-visits', label: 'Visites (Sem)', icon: WarningOutlined },
        { key: '/dashboard-stocks', path: '/dashboard-stocks', label: 'Analyse Stocks', icon: FaChartLine },
      ]
    },
    {
      key: 'section-admin',
      label: 'PARAMÈTRE & ADMIN',
      icon: SettingOutlined,
      children: [
        { key: "/dashboard", path: "/dashboard", label: "Dashboard", icon: DashboardOutlined },

        canViewUsers && { key: "/utilisateurs", path: "/utilisateurs", label: "Utilisateurs", icon: UserOutlined },
        canViewDelegates && { key: "/délégués", path: "/délégués", label: "Délégués", icon: TeamOutlined },
        canViewSupervisors && { key: "/superviseurs", path: "/superviseurs", label: "Superviseurs", icon: ReconciliationOutlined },
        canManageRoles && { key: "/roles", path: "/roles", label: "Rôles", icon: SolutionOutlined },
        canViewAudit && { key: "/audit-logs", path: "/audit-logs", label: "Trace", icon: HistoryOutlined },
        canManageSettings && { key: "/settings", path: "/settings", label: "Paramètres Globaux", icon: SettingOutlined },
      ].filter(Boolean)
    },
    {
      key: 'section-babyworld-clinic',
      label: 'BABYWORLD CLINIC',
      icon: FaHospitalUser,
      children: [
        { key: "/rapports-cliniques", path: "/rapports-cliniques", label: "Intelligence Maternité & Naissances", icon: FaBaby },
        { key: "/stats-cliniques", path: "/stats-cliniques", label: "Intelligence Marketing Clinique", icon: FaChartLine },
      ]
    },
  ];

  const filteredMenuItems = menuItems.map(item => {
    if (isMarketingComm) {
      if (item.key === 'section-babyworld-clinic') {
        return {
          ...item,
          label: 'PLATEFORME CLINIQUE & NAISSANCES' 
        };
      }
      return null; 
    }
    
    if (isCommercial) {
      if (item.key === 'section-analytics') return { ...item, children: item.children.filter(c => c.path === '/analytics/summary') };
      if (item.key === 'section-planning') return { ...item, children: item.children.filter(c => c.path === '/planning/leaves') };

      if (isSyrine && item.key === 'section-rapports') {
        return {
          ...item,
          children: item.children.filter(c => c.path === '/rapports-superviseurs')
        };
      }
      return null;
    }
     
    if (isSupervisor) return item.key === 'section-rapports' ? item : null;
    if (item.children) {
      const authorizedChildren = item.children.filter(Boolean);
      return authorizedChildren.length > 0 ? { ...item, children: authorizedChildren } : null;
    }
    return item;
  }).filter(Boolean);

  const checkIsActive = (item) => {
    if (!item) return false;
    if (item.path && item.path === currentPath) return true;
    if (item.children && item.children.length > 0) return item.children.some(child => checkIsActive(child));
    return false;
  };

  const renderIcon = (IconComponent, color) => {
    if (!IconComponent) return null;
    return (
      <span className="anticon" style={{ marginRight: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px' }}>
        {React.createElement(IconComponent, { style: { fontSize: '16px', color: color } })}
      </span>
    );
  };

  const renderMenuItems = (items) => {
    return items.map((item) => {
      const isActive = checkIsActive(item); 
      if (item.children && item.children.length > 0) {
        return (
          <Menu.SubMenu 
            key={item.key} 
            icon={renderIcon(item.icon, inactiveColor)} 
            title={<span style={{ fontWeight: 'bold', fontSize: '12px', color: inactiveColor }}>{item.label}</span>} 
          >
            {renderMenuItems(item.children)}
          </Menu.SubMenu>
        );
      }
      const isSelfActive = item.path === currentPath;
      const itemColor = isSelfActive ? activeLinkColor : inactiveColor;
      return (
        <Menu.Item 
            key={item.path || item.key} 
            style={{ backgroundColor: 'transparent', borderRadius: '4px' }}
        >
          <Link to={item.path}>
            {renderIcon(item.icon, itemColor)}
            <span style={{ color: itemColor, fontWeight: isSelfActive ? '600' : 'normal' }}>{item.label}</span>
          </Link>
        </Menu.Item>
      );
    });
  };

  return (
    <>
      <div className="brand" style={{ textAlign: 'center', padding: '10px 0' }}>
        <Link to="/rapports"><img src={logoLG} alt="logo" style={{ height: '45px' }} /></Link>
      </div>
      <hr style={{ margin: '10px 24px' }}/>
      
      <style>{`
          .layout-dashboard .ant-menu-inline .ant-menu-item-selected {
            background-color: transparent !important; 
          }
          .ant-menu-item:active, .ant-menu-item-selected {
            background-color: transparent !important;
          }
          .ant-menu-submenu-title:hover, .ant-menu-item:hover {
            background-color: #f5f5f5 !important;
          }
      `}</style>

      <Menu theme="light" mode="inline" inlineIndent={0} selectedKeys={[currentPath]} style={{ borderRight: 0 }}>
        {renderMenuItems(filteredMenuItems)}
      </Menu>
    </>
  );
}

export default Sidenav;